import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon.tsx';
import { vendorsApi } from '../features/vendors/api.ts';
import { t } from '../lib/strings.ts';
import { formatDate } from '@/lib/format';

interface Props {
  vendorId: string;
  hasEmail: boolean;
  credentialsSentAt: string | null;
  /** Compact inline layout for table cells (no heading, minimal status). */
  compact?: boolean;
}

/**
 * "Send login credentials" control. Render only for roles allowed to send
 * (STAFF / ADMIN / SYS_ADMIN) — gate at the call site. Disabled with a hint
 * when the vendor has no email on file.
 */
export function SendCredentials({ vendorId, hasEmail, credentialsSentAt, compact }: Props): JSX.Element {
  const qc = useQueryClient();
  const [sentAt, setSentAt] = useState<string | null>(credentialsSentAt);
  const [ok, setOk] = useState(false);

  const mutation = useMutation({
    mutationFn: () => vendorsApi.sendCredentials(vendorId),
    onSuccess: (data) => {
      setSentAt(data.credentialsSentAt);
      setOk(true);
      void qc.invalidateQueries({ queryKey: ['vendors'] });
      void qc.invalidateQueries({ queryKey: ['vendor', vendorId] });
    },
  });

  const noEmailError =
    mutation.isError &&
    (mutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error === 'no_email';

  if (compact) {
    return (
      <div className="flex flex-col items-start gap-1">
        {!hasEmail ? (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Icon name="unsubscribe" size={14} />
            {t.vendors.credentialsNoEmail}
          </span>
        ) : (
          <>
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="btn-secondary btn-sm gap-1"
            >
              <Icon name="key" size={14} />
              {mutation.isPending
                ? t.vendors.sendingCredentials
                : sentAt
                  ? t.vendorEdit.resendCredentials
                  : t.vendors.sendCredentials}
            </button>
            {ok && !mutation.isError ? (
              <span className="flex items-center gap-1 text-xs text-success">
                <Icon name="check_circle" size={14} />
                {t.vendors.credentialsSentOk}
              </span>
            ) : mutation.isError ? (
              <span className="text-xs text-destructive">{t.vendors.credentialsError}</span>
            ) : sentAt ? (
              <span className="text-xs text-muted-foreground num">{formatDate(sentAt)}</span>
            ) : (
              <span className="text-xs text-muted-foreground">{t.vendors.credentialsNotSent}</span>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="status-label text-muted-foreground">{t.vendorEdit.loginHeading}</h3>

      {/* Status line */}
      {!hasEmail ? (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Icon name="unsubscribe" size={14} />
          {t.vendorEdit.credentialsNoEmail}
        </p>
      ) : sentAt ? (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Icon name="check_circle" size={14} className="text-success" />
          {t.vendorEdit.credentialsSent(formatDate(sentAt))}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">{t.vendorEdit.credentialsNotSent}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!hasEmail || mutation.isPending}
          className="btn-secondary btn-sm gap-1.5"
        >
          <Icon name="key" size={14} />
          {mutation.isPending
            ? t.vendorEdit.sendingCredentials
            : sentAt
              ? t.vendorEdit.resendCredentials
              : t.vendorEdit.sendCredentials}
        </button>
        {ok && !mutation.isError && (
          <span className="text-sm text-success" role="status">{t.vendorEdit.credentialsSentOk}</span>
        )}
        {mutation.isError && (
          <span className="text-sm text-destructive" role="alert">
            {noEmailError ? t.vendorEdit.credentialsNoEmail : t.vendorEdit.credentialsError}
          </span>
        )}
      </div>
    </div>
  );
}
